import React, { useRef, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { submitChallenge } from "../../../features/cohort/cohortSlice";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { vscDarkPlus } from "react-syntax-highlighter/dist/esm/styles/prism";
import ContinuousCodeEditor from "../../Lesson/ContinuousCodeEditor";
import TaskResultPanel from "../../Lesson/TaskResultPanel";
import TaskDebugOutput from "../../Lesson/TaskDebugOutput";

function ChallengeWorkspace({ challenge, progress }) {
  const dispatch = useDispatch();
  const editorRef = useRef(null);
  const [outputTab, setOutputTab] = useState("tests");
  const { testResults, debugLogs, isSubmitting } = useSelector((state) => state.cohort);

  // Switch to tests tab when new results arrive
  useEffect(() => {
    if (testResults) {
      setOutputTab("tests");
    }
  }, [testResults]);

  const handleSubmit = () => {
    if (!editorRef.current || isSubmitting) return;

    const code = editorRef.current.getCurrentContent();

    if (!code || code.trim() === "") {
      alert("Please write some code before submitting.");
      return;
    }

    dispatch(
      submitChallenge({
        challengeId: challenge.id,
        apexBody: code,
      })
    );
  };

  // Create lesson object compatible with ContinuousCodeEditor
  const lessonForEditor = {
    _id: challenge.id,
    boilerPlateCode: challenge.boilerPlateCode,
    progress: progress ? [{ lastSubmission: progress.lastSubmission }] : [],
  };

  return (
    <div className="flex-1 flex flex-col md:flex-row overflow-hidden">
      {/* Instructions Panel */}
      <div className="w-full md:w-2/5 border-r border-zinc-800 flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-800 bg-zinc-900/50">
          <h2 className="font-display font-semibold text-zinc-200">{challenge.name}</h2>
          {progress && progress.status && (
            <div className="flex items-center gap-2 mt-1">
              <span
                className={`text-xs font-mono ${
                  progress.status === "Completed"
                    ? "text-emerald-400"
                    : progress.status === "In Progress"
                    ? "text-amber-400"
                    : "text-zinc-500"
                }`}
              >
                {progress.status}
              </span>
              {progress.attemptCounter > 0 && (
                <span className="text-xs text-zinc-500">
                  • {progress.attemptCounter} attempt{progress.attemptCounter !== 1 ? "s" : ""}
                </span>
              )}
            </div>
          )}
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
              code({ node, inline, className, children, ...props }) {
                const match = /language-(\w+)/.exec(className || "");
                return !inline && match ? (
                  <SyntaxHighlighter
                    children={String(children).replace(/\n$/, "")}
                    style={vscDarkPlus}
                    language={match[1]}
                    PreTag="div"
                    {...props}
                  />
                ) : (
                  <code className={className} {...props}>
                    {children}
                  </code>
                );
              },
            }}
          >
            {challenge.lesson}
          </ReactMarkdown>
        </div>
      </div>

      {/* IDE Panel */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Code Editor */}
        <div className="h-96 overflow-hidden">
          <ContinuousCodeEditor ref={editorRef} lesson={lessonForEditor} />
        </div>

        {/* Output Panel - Test Results & Debug Logs */}
        <div className="border-t border-zinc-800 bg-zinc-900/80 flex flex-col flex-1 min-h-0">
          {/* Tabs */}
          <div className="flex items-center justify-between border-b border-zinc-800 px-2">
            <div className="flex">
              <button
                onClick={() => setOutputTab("tests")}
                className={`px-4 py-2 text-sm font-display font-medium transition-colors ${
                  outputTab === "tests"
                    ? "text-amber-400 border-b-2 border-amber-400"
                    : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                Test Results
                {testResults && (
                  <span
                    className={`ml-2 text-xs ${
                      testResults.success ? "text-emerald-400" : "text-red-400"
                    }`}
                  >
                    {testResults.success ? "✓ Passed" : "✗ Failed"}
                  </span>
                )}
              </button>
              <button
                onClick={() => setOutputTab("debug")}
                className={`px-4 py-2 text-sm font-display font-medium transition-colors ${
                  outputTab === "debug"
                    ? "text-amber-400 border-b-2 border-amber-400"
                    : "text-zinc-500 hover:text-zinc-300"
                }`}
              >
                Debug Logs
              </button>
            </div>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white font-display font-medium px-4 py-1.5 rounded-lg text-sm transition-colors my-1 mr-2"
            >
              {isSubmitting ? "Running..." : "Run Tests"}
            </button>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {outputTab === "tests" && (
              testResults ? (
                <TaskResultPanel results={{ data: testResults }} />
              ) : (
                <div className="flex items-center justify-center h-full text-zinc-500 font-display text-sm">
                  <p>Test results will appear here once you run your tests</p>
                </div>
              )
            )}
            {outputTab === "debug" && (
              debugLogs ? (
                <TaskDebugOutput logs={debugLogs} />
              ) : (
                <div className="flex items-center justify-center h-full text-zinc-500 font-display text-sm">
                  <p>Debug output will appear here once you run your tests</p>
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ChallengeWorkspace;
